import {
  ClientMessage,
  ServerMessage,
  UserInfo,
} from './protocol';

type MessageHandler = (msg: ServerMessage) => void;
type BinaryHandler = (data: ArrayBuffer) => void;

export class RoomClient extends EventTarget {
  private ws: WebSocket | null = null;
  private _isConnected = false;
  private _isHost = false;
  private _roomId = '';
  private _nickname = '';
  private _users: UserInfo[] = [];
  private messageHandlers: MessageHandler[] = [];
  private binaryHandlers: BinaryHandler[] = [];
  private shouldAutoReconnect = false;
  private manualDisconnect = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelayMs = 500;
  private readonly maxReconnectDelayMs = 8000;
  private isReconnecting = false;

  get isConnected() { return this._isConnected; }
  get isHost() { return this._isHost; }
  get roomId() { return this._roomId; }
  get nickname() { return this._nickname; }
  get users() { return this._users; }

  constructor() {
    super();
    this.bindLifecycleReconnect();
  }

  /** Create a new room via REST, returns roomId */
  async createRoom(): Promise<string> {
    const baseUrl = this.getBaseUrl();
    const res = await fetch(`${baseUrl}/api/rooms`, { method: 'POST' });
    const data = await res.json();
    return data.roomId;
  }

  /** Connect to a room via WebSocket */
  connect(roomId: string, nickname: string, isHost: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      this._roomId = roomId;
      this._nickname = nickname;
      this._isHost = isHost;
      this.shouldAutoReconnect = true;
      this.manualDisconnect = false;
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.clearReconnectTimer();
      this.openSocket(resolve, reject);
    });
  }

  disconnect() {
    this.shouldAutoReconnect = false;
    this.manualDisconnect = true;
    this.clearReconnectTimer();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this._isConnected = false;
    }
  }

  /** Send a JSON message */
  send(msg: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /** Send binary data (game frames) */
  sendBinary(data: ArrayBuffer) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  /** Send chat message */
  chat(message: string) {
    this.send({ type: 'chat', message });
  }

  /** Send game setup (opaque data) */
  sendGameSetup(gameId: string, data: Record<string, unknown>) {
    this.send({ type: 'game_setup', gameId, data });
  }

  /** Send game start */
  sendGameStart() {
    this.send({ type: 'game_start' });
  }

  /** Send game finished (opaque results) */
  sendGameFinished(data: Record<string, unknown>) {
    this.send({ type: 'game_finished', data });
  }

  /** Send game reset */
  sendGameReset() {
    this.send({ type: 'game_reset' });
  }

  /** Send game effect (opaque data) */
  sendGameEffect(data: Record<string, unknown>) {
    this.send({ type: 'game_effect', data });
  }

  /** Register handler for server messages */
  onMessage(handler: MessageHandler) {
    this.messageHandlers.push(handler);
  }

  /** Register handler for binary data */
  onBinary(handler: BinaryHandler) {
    this.binaryHandlers.push(handler);
  }

  private handleMessage(msg: ServerMessage) {
    // 서버 ping에 자동 pong 응답
    if ((msg as any).type === 'ping') {
      this.send({ type: 'pong' } as any);
      return;
    }

    switch (msg.type) {
      case 'joined':
        this._users = msg.users;
        if (this.isReconnecting) {
          this.isReconnecting = false;
          this.reconnectAttempts = 0;
          this.clearReconnectTimer();
          this.dispatchEvent(new CustomEvent('reconnected'));
        }
        break;
      case 'user_joined':
      case 'user_left':
        this._users = msg.users;
        break;
    }
    this.messageHandlers.forEach(h => h(msg));
    this.dispatchEvent(new CustomEvent(msg.type, { detail: msg }));
  }

  private openSocket(resolve?: () => void, reject?: (err: Error) => void) {
    const wsUrl = this.getWsUrl();
    this.ws = new WebSocket(`${wsUrl}/ws/${this._roomId}`);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this._isConnected = true;
      this.send({ type: 'join', nickname: this._nickname, isHost: this._isHost });
      if (resolve) resolve();
    };

    this.ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.binaryHandlers.forEach(h => h(event.data));
        return;
      }
      try {
        const msg: ServerMessage = JSON.parse(event.data);
        this.handleMessage(msg);
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    this.ws.onclose = () => {
      this._isConnected = false;
      if (this.manualDisconnect || !this.shouldAutoReconnect) {
        this.dispatchEvent(new CustomEvent('disconnected'));
        return;
      }
      this.dispatchEvent(new CustomEvent('disconnected'));
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      if (reject) {
        reject(new Error('WebSocket connection failed'));
      }
    };
  }

  private scheduleReconnect() {
    if (!this.shouldAutoReconnect || this.manualDisconnect) return;
    if (this.reconnectTimer) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    const attempt = this.reconnectAttempts + 1;
    const delay = Math.min(
      this.maxReconnectDelayMs,
      this.baseReconnectDelayMs * (2 ** (attempt - 1)),
    );

    this.reconnectAttempts = attempt;
    this.isReconnecting = true;
    this.dispatchEvent(new CustomEvent('reconnecting', { detail: { attempt, delay } }));

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.shouldAutoReconnect || this.manualDisconnect || this._isConnected) return;
      this.openSocket();
    }, delay);
  }

  private tryImmediateReconnect() {
    if (!this.shouldAutoReconnect || this.manualDisconnect || this._isConnected) return;
    if (!this._roomId || !this._nickname) return;

    const state = this.ws?.readyState;
    if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) return;

    this.isReconnecting = true;
    this.clearReconnectTimer();
    this.openSocket();
  }

  private clearReconnectTimer() {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private bindLifecycleReconnect() {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        this.tryImmediateReconnect();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', () => this.tryImmediateReconnect());
    window.addEventListener('pageshow', () => this.tryImmediateReconnect());
  }

  private getBaseUrl(): string {
    const { protocol, hostname, port } = window.location;
    const backendPort = hostname === 'localhost' ? ':8030' : (port ? `:${port}` : '');
    return `${protocol}//${hostname}${backendPort}`;
  }

  private getWsUrl(): string {
    const { protocol, hostname, port } = window.location;
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
    const backendPort = hostname === 'localhost' ? ':8030' : (port ? `:${port}` : '');
    return `${wsProtocol}//${hostname}${backendPort}`;
  }
}
